import React, { useState, useEffect } from "react";
import InfoIcon from "./InfoIcon";
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { logAIRequest, checkAIRequestLimit } from "../services/aiRequestService";
import { useSubscription } from "../hooks/useSubscription";
// Fast local topic similarity using Jaccard index
function jaccardSimilarity(str1, str2) {
  const set1 = new Set(str1.toLowerCase().split(/\W+/).filter(Boolean));
  const set2 = new Set(str2.toLowerCase().split(/\W+/).filter(Boolean));
  const intersection = new Set([...set1].filter(x => set2.has(x)));
  const union = new Set([...set1, ...set2]);
  return union.size === 0 ? 0 : intersection.size / union.size;
}
import { useHeaderContext } from "../context/HeaderContext";
import { supabase } from "../services/supabase";

// Configure marked options
marked.setOptions({
  breaks: true, // Convert line breaks to <br>
  gfm: true,    // Enable GitHub Flavored Markdown
});

// Function to convert Markdown to HTML with XSS protection
function parseMarkdown(text) {
  if (!text) return "";
  const html = marked(text);
  return DOMPurify.sanitize(html);
}

const GroqDrawer = ({ isOpen, onClose, setQuery, onResponse, systemPromptKey = "system_prompt" }) => {
  // Access persistent header context
  const { groqContext, location } = useHeaderContext();
  const { tier } = useSubscription();
  // Checkbox state for 'Use current context'
  const [useContext, setUseContext] = useState(true);
  // Auth and LLM/chat state
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // Allow parent to set the query field
  React.useEffect(() => {
    if (typeof setQuery === "function") {
      setQuery((q) => setSearchTerm(q));
    }
  }, [setQuery]);
  const [groqResponse, setGroqResponse] = useState("");
  // const [groqPayload, setGroqPayload] = useState(null); // Debug state removed
  const [conversation, setConversation] = useState([]); // short-term memory
  const [groqLoading, setGroqLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isMemoryOpen, setIsMemoryOpen] = useState(false);

  // Check session and subscribe to auth changes
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data?.session?.user) {
        setUser(data.session.user);
        loadMemory(data.session.user.id);
      }
    });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      const u = session?.user || null;
      setUser(u);
      if (u) loadMemory(u.id);
      else setConversation([]);
    });

    return () => listener.subscription.unsubscribe();
    // eslint-disable-next-line
  }, []);

  const ensureUserSettingsRow = async (userId) => {
    const { data } = await supabase
      .from("user_settings")
      .select("user_id")
      .eq("user_id", userId)
      .single();

    if (!data) {
      const { error } = await supabase
        .from("user_settings")
        .insert([{ user_id: userId, memory: [], preferences: {} }]);
      if (error) console.error("Error creating user_settings row:", error.message);
    }
  };

  const loadMemory = async (userId) => {
    await ensureUserSettingsRow(userId);
    const { data, error } = await supabase
      .from("user_settings")
      .select("memory")
      .eq("user_id", userId)
      .single();
    if (error && error.code !== "PGRST116") console.error(error);
    else setConversation(Array.isArray(data?.memory) ? data.memory : []);
  };

  const handleLogin = async () => {
    setError(null);
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) setError(error.message);
    else setUser(data.user);
  };

  const handleSignup = async () => {
    setError(null);
    const { data, error } = await supabase.auth.signUp({ email, password });
    if (error) setError(error.message);
    else alert("Sign up successful! Check your email to confirm.");
  };

  const callGroq = async () => {
    if (!searchTerm.trim() || !user) return;
    setGroqLoading(true);
    setError(null);

    try {
      // Check AI request limit for free tier users
      const limitCheck = await checkAIRequestLimit(user.id, tier);
      if (!limitCheck.allowed) {
        setError(`You've reached your daily limit. You've used ${limitCheck.requestsToday}/${limitCheck.limit} Ask YOGi requests today. Upgrade to Premium for unlimited requests.`);
        setGroqLoading(false);
        return;
      }

      // Warn if getting close to limit
      if (limitCheck.remaining === 1) {
        alert(`⚠️ You have ${limitCheck.remaining} Ask YOGi request left today`);
      }

      await ensureUserSettingsRow(user.id);

      const { data: configData, error: configError } = await supabase
        .from("app_config")
        .select("value")
        .eq("key", systemPromptKey)
        .single();
      if (configError) throw configError;
      const systemPrompt = configData?.value || "You are a helpful assistant for outdoor recreation data.";

      // If useContext is checked and groqContext exists, append context to the query
      let userQuery = searchTerm;
      if (useContext && groqContext && groqContext.trim().length > 0) {
        userQuery += `\n\n[Context]\n${groqContext}`;
      }

      // Topic guardrail: Only include conversation if similar
      let includeMemory = false;
      if (conversation.length > 0) {
        const lastPrompt = conversation[conversation.length - 1].prompt;
        const similarity = jaccardSimilarity(lastPrompt, searchTerm);
        includeMemory = similarity >= 0.3; // Adjust threshold as needed
      }

      // Build messages array - conditionally include system prompt
      let messages = [];
      
      // Only add system message if systemPrompt exists and isn't empty
      if (systemPrompt && systemPrompt.trim()) {
        messages.push({ role: "system", content: systemPrompt });
      }
      
      messages.push({ role: "user", content: userQuery });
      
      if (includeMemory) {
        messages = [
          { role: "system", content: systemPrompt },
          ...conversation.map((c) => ({ role: "user", content: c.prompt })),
          { role: "user", content: userQuery },
        ];
      }




      const res = await fetch("https://bylolucjhzsbbouoalah.functions.supabase.co/groq-chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "llama-3.3-70b-versatile",
          messages,
        }),
      });

      const data = await res.json();
      // Log the response after receiving

      const responseText = data?.choices?.[0]?.message?.content || "No response";
      setGroqResponse(responseText);
      
      // Log the AI request for rate limiting
      await logAIRequest(user.id);
      
      // Notify parent component of the response
      if (typeof onResponse === "function") {
        onResponse(responseText);
      }

      const newMemory = [...conversation, { prompt: searchTerm, response: responseText }].slice(-3);
      setConversation(newMemory);

      const { error: upsertError } = await supabase
        .from("user_settings")
        .upsert([{ user_id: user.id, memory: newMemory }], { onConflict: "user_id" });
      if (upsertError) console.error("Error saving memory:", upsertError.message);
    } catch (err) {
      setError(err.message);
    } finally {
      setGroqLoading(false);
      setSearchTerm(""); // Clear search box after submit
    }
  };

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40"
          onClick={onClose}
        />
      )}

      {/* Drawer panel */}
      <div
        className={`fixed right-0 top-0 h-full bg-white dark:bg-gray-800 shadow-xl z-50 transform transition-transform duration-300
        ${isOpen ? "translate-x-0" : "translate-x-full"} w-full sm:w-96`}
      >
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">YOGi Assistant</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 text-xl font-bold"
          >
            ×
          </button>
        </div>

        {/* Body */}
  <div className="p-4 overflow-y-auto h-[calc(100%-60px)]">
          {!user ? (
            <div className="flex flex-col gap-4">
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="border p-3 rounded w-full text-gray-900 bg-white dark:text-gray-100 dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              />
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="border p-3 rounded w-full text-gray-900 bg-white dark:text-gray-100 dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              />
              <div className="flex gap-2">
                <button
                  onClick={handleLogin}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded flex-1 font-medium transition-all duration-200"
                >
                  Login
                </button>
                <button
                  onClick={handleSignup}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded flex-1 font-medium transition-all duration-200"
                >
                  Sign Up
                </button>
              </div>
              {error && <p className="text-red-600 mt-2 text-center">{error}</p>}
            </div>
          ) : (
            <>
              <textarea
                value={searchTerm}
                onChange={e => {
                  setSearchTerm(e.target.value);
                  // Auto-grow logic
                  e.target.style.height = 'auto';
                  e.target.style.height = e.target.scrollHeight + 'px';
                }}
                className="border p-2 rounded w-full mt-2 text-gray-900 dark:text-gray-100 dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none"
                placeholder="Type your question here..."
                rows={1}
                style={{ overflow: 'hidden' }}
                onInput={e => {
                  e.target.style.height = 'auto';
                  e.target.style.height = e.target.scrollHeight + 'px';
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    callGroq();
                  }
                }}
              />
              {/* Use current context checkbox */}
              <div className="flex items-center gap-2 mt-2 mb-2">
                <input
                  type="checkbox"
                  id="use-context-checkbox"
                  checked={useContext}
                  onChange={e => setUseContext(e.target.checked)}
                  className="form-checkbox h-4 w-4 text-blue-600 transition duration-150"
                />
                <label htmlFor="use-context-checkbox" className="text-sm text-gray-800 dark:text-gray-200 select-none flex items-center gap-1">
                  Use Field Notes for context
                  <span className="relative group">
                    <InfoIcon />
                    <span className="absolute left-1/2 top-full -translate-x-1/2 mt-2 max-w-[250px] w-max bg-gray-800 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 shadow-lg whitespace-normal break-words text-left">
                      Field Notes provide context about your current location, vendor and other relevant information. You can view your Field Notes in the top navbar by clicking the "Field Notes" button.
                    </span>
                  </span>
                </label>
              </div>
              <button
                onClick={callGroq}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 mt-2 rounded font-medium transition-all duration-200 w-full"
                disabled={groqLoading}
              >
                {groqLoading ? "Calling Groq..." : "Submit"}
              </button>
              {error && <p className="text-red-600 mt-2 text-center">{error}</p>}




              {/* Groq Response Display */}
              <div className="my-4">
                {groqResponse ? (
                  <div
                    className="text-gray-700 dark:text-gray-200 prose prose-sm max-w-none 
                               [&>p]:mb-4 [&>p]:leading-relaxed
                               [&>ul]:mb-4 [&>ul]:pl-6 [&>li]:mb-2 
                               [&>ol]:mb-4 [&>ol]:pl-6 
                               [&>h1]:mb-4 [&>h1]:mt-6 [&>h1]:font-bold [&>h1]:text-lg
                               [&>h2]:mb-4 [&>h2]:mt-6 [&>h2]:font-bold [&>h2]:text-base
                               [&>h3]:mb-3 [&>h3]:mt-4 [&>h3]:font-semibold
                               [&>strong]:font-semibold [&>strong]:text-gray-900 [&>strong]:dark:text-white
                               [&>em]:italic [&>em]:text-gray-600 [&>em]:dark:text-gray-300
                               [&>blockquote]:border-l-4 [&>blockquote]:border-blue-500 [&>blockquote]:pl-4 [&>blockquote]:italic [&>blockquote]:my-4
                               [&>code]:bg-gray-100 [&>code]:dark:bg-gray-800 [&>code]:px-2 [&>code]:py-1 [&>code]:rounded [&>code]:text-sm [&>code]:font-mono
                               [&>pre]:bg-gray-100 [&>pre]:dark:bg-gray-800 [&>pre]:p-4 [&>pre]:rounded [&>pre]:overflow-x-auto [&>pre]:my-4
                               [&>a]:text-blue-600 [&>a]:dark:text-blue-400 [&>a]:underline [&>a]:break-all"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(parseMarkdown(groqResponse)) }}
                  />
                ) : (
                  <p className="text-gray-700 dark:text-gray-200 min-h-[2rem]">Ask Your Online Guide...</p>
                )}
              </div>

              {/* Collapsible Memory */}
              <div className="mt-6">
                <button
                  onClick={() => setIsMemoryOpen(!isMemoryOpen)}
                  className="w-full text-left px-4 py-2 font-semibold bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-t"
                >
                  Conversation Memory {isMemoryOpen ? "\u25b2" : "\u25bc"}
                </button>
                {isMemoryOpen && conversation.length > 0 && (
                  <ul className="list-disc pl-5 py-2 space-y-2">
                    {conversation.slice().reverse().map((item, idx) => (
                      <li key={idx}>
                        <p className="font-semibold">You: {item.prompt}</p>
                        <p className="text-sm">Groq: {item.response}</p>
                      </li>
                    ))}
                  </ul>
                )}
                {isMemoryOpen && conversation.length === 0 && (
                  <p className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">No conversation memory yet.</p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default GroqDrawer;
